// User-display lookup hooks. Resolve `updatedBy` / `createdBy`
// user IDs to a minimal `{ id, name, organisation }` shape for
// rendering on tracking meta lines — no email / role / status.
//
// Caching strategy: `useUserDisplays([ids])` makes ONE batched fetch
// keyed on the sorted id list. On success, each resolved user is also
// written to the `['users', 'byId', id]` cache key so a subsequent
// `useUserDisplay(id)` hits the warm cache without a second round-
// trip. Stale time is 5 minutes — names / orgs change rarely, and
// the short TTL keeps us honest after a user rename.

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { usersApi } from "../services/api";
import type { UserDisplay, UserDisplayBatchResponse } from "../types";

const STALE_MS = 5 * 60 * 1000;

function sortedKey(ids: string[]): string {
  // Deduplicate + sort so the cache key is order-independent.
  // Duplicates are cheap (single network call regardless), but the
  // React Query cache key must be stable across render orderings.
  const set = new Set(ids.filter((id) => id && id.length > 0));
  return Array.from(set).sort().join(",");
}

export interface UseUserDisplaysResult {
  data: Map<string, UserDisplay>;
  isPending: boolean;
}

export function useUserDisplays(ids: string[]): UseUserDisplaysResult {
  const queryClient = useQueryClient();
  const key = useMemo(() => sortedKey(ids), [ids]);
  const hasIds = key.length > 0;

  const query = useQuery<UserDisplayBatchResponse>({
    queryKey: ["users", "batch", key],
    queryFn: async () => {
      const res = await usersApi.batchLookup(
        key.length > 0 ? key.split(",") : [],
      );
      // Write each resolved user into the by-id cache synchronously
      // with fetch resolution so a subsequent single-id hook mount
      // reads warm via `initialData`. Doing this inside queryFn (not
      // useEffect) avoids the commit-phase race that previously let
      // the single-id hook fire a second round-trip.
      for (const user of res.users) {
        queryClient.setQueryData<UserDisplay>(
          ["users", "byId", user.id],
          user,
        );
      }
      return res;
    },
    enabled: hasIds,
    staleTime: STALE_MS,
  });

  const map = useMemo(() => {
    const out = new Map<string, UserDisplay>();
    const users = query.data?.users ?? [];
    for (const user of users) out.set(user.id, user);
    return out;
  }, [query.data]);

  return {
    data: map,
    // isPending only while we actually have IDs to resolve. Empty
    // input returns an immediate `{ data: empty map, isPending: false }`.
    isPending: hasIds && query.isPending,
  };
}

export interface UseUserDisplayResult {
  data: UserDisplay | undefined;
  isPending: boolean;
}

export function useUserDisplay(id: string | undefined): UseUserDisplayResult {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ["users", "byId", id ?? ""],
    queryFn: async () => {
      const res = await usersApi.batchLookup([id as string]);
      return res.users[0] ?? null;
    },
    enabled: Boolean(id),
    staleTime: STALE_MS,
    // If a prior `useUserDisplays` batch already resolved this id,
    // the by-id cache has been primed. Seed `initialData` from it so
    // React Query treats the entry as already-fetched and skips the
    // queryFn — no second network round-trip for ids the batch hook
    // warmed.
    initialData: () => {
      if (!id) return undefined;
      return queryClient.getQueryData<UserDisplay>(["users", "byId", id]);
    },
    initialDataUpdatedAt: () => {
      if (!id) return 0;
      return (
        queryClient.getQueryState(["users", "byId", id])?.dataUpdatedAt ?? 0
      );
    },
  });

  return {
    data: query.data ?? undefined,
    isPending: Boolean(id) && query.isPending,
  };
}
